import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, X, Sparkles, Mail, ChevronLeft, Lock, ShieldCheck, Crown,
  Building2, Users, Clock, Tag, ArrowRight,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- helpers ----------

function getToken() {
  try { return localStorage.getItem("gs_token"); } catch { return null; }
}

function formatEUR(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

function FeatureRow({ included, label }) {
  return (
    <li className="flex items-start gap-2 py-1.5 text-sm">
      {included ? (
        <Check className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
      ) : (
        <X className="w-4 h-4 mt-0.5 text-slate-300 shrink-0" />
      )}
      <span className={included ? "text-slate-700" : "text-slate-400 line-through"}>{label}</span>
    </li>
  );
}

function featureLabels(plan) {
  // Matrice esatta da listino (Feb 2026):
  return [
    { key: "pwa", on: true, label: "App Web (PWA) brandizzata" },
    { key: "qr", on: plan.qr_branded, label: "QR Code personalizzato" },
    { key: "upload", on: true, label: "Invio foto e documenti" },
    { key: "gps", on: true, label: "Geolocalizzazione automatica" },
    { key: "wizard", on: true, label: "Apertura pratica guidata" },
    { key: "dash", on: true, label: "Dashboard pratiche" },
    { key: "notif", on: true, label: "Notifiche cliente" },
    { key: "branding", on: plan.branding_custom, label: "Personalizzazione logo e colori" },
    { key: "users", on: !!plan.feat_gestione_utenti, label: "Gestione utenti" },
    { key: "workflow", on: !!plan.feat_workflow_avanzati, label: "Workflow avanzati" },
    { key: "stats", on: !!plan.feat_statistiche_operative, label: "Statistiche operative" },
    { key: "multisede", on: !!plan.feat_multi_sede, label: "Multi-sede" },
    { key: "dashcentral", on: !!plan.feat_dashboard_centrale, label: "Dashboard centrale" },
    { key: "filiali", on: !!plan.feat_controllo_filiali, label: "Controllo filiali" },
    { key: "report", on: !!plan.feat_report_aggregati, label: "Report aggregati" },
    { key: "brandcentral", on: !!plan.feat_branding_centralizzato, label: "Branding centralizzato" },
    { key: "priority", on: !!plan.feat_assistenza_prioritaria, label: "Assistenza prioritaria" },
  ];
}

// Minimal discount indicator — shows ONLY the percentage, no schedule / countdown / explanation.
// This is intentional: the consultant must see the discount change but never be able to
// predict / program when it will change.
function DiscountStrip({ discount }) {
  if (!discount || !discount.discount_pct) return null;
  return (
    <div
      data-testid="pricing-discount-strip"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#F77F2A] via-[#FF5757] to-[#E11D48] text-white p-5 sm:p-6 mb-10 shadow-xl"
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.45) 1px, transparent 1px)", backgroundSize: "12px 12px" }} />
      <div className="relative flex items-center gap-4">
        <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/15 backdrop-blur">
          <Tag className="w-7 h-7 sm:w-8 sm:h-8" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] sm:text-[11px] font-black tracking-widest uppercase opacity-90">Offerta attiva</div>
          <div className="text-xl sm:text-2xl font-black leading-tight">
            Sconto <span className="bg-white text-[#E11D48] px-2 py-0.5 rounded-lg mx-1">−{discount.discount_pct}%</span> sul piano annuale
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- main page ----------

export default function PricingPage() {
  const [publicData, setPublicData] = useState(null);
  const [offer, setOffer] = useState(null);            // { discount, plans }
  const [cycle, setCycle] = useState("annual");
  const [loading, setLoading] = useState(true);
  const token = getToken();

  useEffect(() => {
    let cancelled = false;
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    (async () => {
      try {
        setLoading(true);
        if (token) {
          const r = await axios.get(`${API}/pricing/my-offer`, { headers });
          if (cancelled) return;
          setOffer(r.data);
        } else {
          const r = await axios.get(`${API}/pricing/plans`);
          if (cancelled) return;
          setPublicData(r.data);
        }
      } catch (_e) {
        try {
          const r2 = await axios.get(`${API}/pricing/plans`);
          if (cancelled) return;
          setPublicData(r2.data);
        } catch (_e2) {
          /* ignore — page will show empty state */
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const isLogged = !!offer;
  const plans = isLogged ? offer.plans : (publicData?.plans || []);
  const discount = offer?.discount;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8F2] via-white to-[#EFF4FF]" data-testid="pricing-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#2456C6]" data-testid="pricing-back-home">
            <ChevronLeft className="w-4 h-4" /> Torna alla home
          </Link>
          {!isLogged ? (
            <Link to="/login" className="text-sm font-bold text-[#2456C6] hover:underline" data-testid="pricing-login-link">
              Accedi
            </Link>
          ) : (
            <Link to="/dashboard" className="text-sm font-bold text-[#2456C6] hover:underline" data-testid="pricing-dashboard-link">
              Vai alla dashboard
            </Link>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        {/* Title */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F77F2A]/10 text-[#F77F2A] text-xs font-bold mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            {isLogged ? "IL TUO LISTINO PERSONALIZZATO" : "PIANI PROFESSIONAL & ENTERPRISE"}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1E293B] tracking-tight mb-4">
            {isLogged ? "Il tuo listino" : "Costruito per chi non ha tempo da perdere"}
          </h1>
          <p className="text-base sm:text-lg text-slate-600">
            {isLogged
              ? "Tutti i piani includono 30 giorni di prova gratuita. Senza vincoli, disdici quando vuoi."
              : "Per visionare prezzi e offerte registrati gratuitamente. Tutti i piani includono 30 giorni di prova."}
          </p>
        </div>

        {/* Discount indicator (logged only) — minimal, no schedule info */}
        {isLogged && <DiscountStrip discount={discount} />}

        {/* Public CTA strip (not logged) */}
        {!isLogged && (
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#2456C6] text-white p-6 sm:p-10 mb-10 shadow-2xl" data-testid="pricing-public-cta">
            <div className="absolute inset-0 opacity-20 pointer-events-none"
                 style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.4) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] font-black tracking-widest uppercase">
                  <Lock className="w-3.5 h-3.5" /> LISTINO RISERVATO
                </div>
                <h2 className="mt-3 text-2xl sm:text-3xl font-black leading-tight">
                  Prezzi e sconti visibili solo dopo la registrazione
                </h2>
                <p className="mt-2 text-sm sm:text-base text-white/80 max-w-2xl">
                  Tutela il tuo studio: l&apos;accesso al listino è riservato ai consulenti registrati. Tutti i piani includono <b>30 giorni di prova gratuita</b>.
                </p>
              </div>
              <div className="flex flex-col gap-3 w-full md:w-auto">
                <Link
                  to="/register"
                  data-testid="pricing-register-cta"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#F77F2A] hover:bg-[#E26F1A] text-white font-black text-sm shadow-lg shadow-orange-500/30"
                >
                  Registrati gratis e vedi i prezzi <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  data-testid="pricing-login-cta"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur text-white font-bold text-sm border border-white/20"
                >
                  Sono già registrato
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Cycle switch (only when prices are visible) */}
        {isLogged && (
          <div className="flex justify-center mb-10" data-testid="pricing-cycle-switch">
            <div className="inline-flex items-center gap-1 p-1 bg-white border border-slate-200 rounded-full shadow-sm">
              <button
                onClick={() => setCycle("monthly")}
                data-testid="cycle-monthly"
                className={`px-5 py-2 rounded-full text-sm font-bold transition ${
                  cycle === "monthly" ? "bg-[#1E293B] text-white" : "text-slate-600 hover:text-[#1E293B]"
                }`}
              >
                Mensile
              </button>
              <button
                onClick={() => setCycle("annual")}
                data-testid="cycle-annual"
                className={`px-5 py-2 rounded-full text-sm font-bold transition inline-flex items-center gap-2 ${
                  cycle === "annual" ? "bg-[#1E293B] text-white" : "text-slate-600 hover:text-[#1E293B]"
                }`}
              >
                Annuale
                {discount?.discount_pct > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-black ${
                    cycle === "annual" ? "bg-[#F77F2A] text-white" : "bg-[#F77F2A]/15 text-[#F77F2A]"
                  }`}>
                    −{discount.discount_pct}%
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Plan cards */}
        {loading ? (
          <div className="text-center py-20 text-slate-500">Caricamento piani…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 max-w-6xl mx-auto">
            {plans.map((plan) => {
              const isPopular = plan.highlight;
              const isEnterprise = plan.is_enterprise;
              return (
                <div
                  key={plan.id}
                  data-testid={`pricing-card-${plan.id}`}
                  className={`relative rounded-3xl p-7 sm:p-8 border bg-white shadow-sm flex flex-col ${
                    isPopular
                      ? "border-[#F77F2A] shadow-xl shadow-[#F77F2A]/10 lg:-translate-y-3 scale-[1.02]"
                      : "border-slate-200"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#F77F2A] text-white text-[11px] font-black tracking-wider rounded-full">
                      PIÙ SCELTO
                    </div>
                  )}
                  {isEnterprise && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#1E293B] text-white text-[11px] font-black tracking-wider rounded-full inline-flex items-center gap-1">
                      <Crown className="w-3 h-3" /> MULTI-SEDE
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-[#2456C6]">
                    {isEnterprise ? <Building2 className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                    <h3 className="text-2xl font-black text-[#1E293B]" data-testid={`plan-name-${plan.id}`}>{plan.name}</h3>
                  </div>
                  {plan.tagline && <p className="text-sm text-slate-500 mt-1">{plan.tagline}</p>}

                  {/* Price block */}
                  <div className="mt-5">
                    {!isLogged ? (
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-black text-slate-300 select-none blur-[3px]" data-testid={`plan-price-locked-${plan.id}`}>
                          €•••
                        </span>
                        <span className="text-sm text-slate-400">/ mese</span>
                      </div>
                    ) : cycle === "annual" ? (
                      <PriceBlockAnnual plan={plan} />
                    ) : (
                      <PriceBlockMonthly plan={plan} />
                    )}
                    {isEnterprise && (
                      <div className="mt-1 text-[11px] text-slate-500 font-bold uppercase tracking-wider">sede principale · ogni filiale +€29/mese</div>
                    )}
                  </div>

                  <div className="my-5 h-px bg-slate-100" />

                  {/* 30-day free trial badge */}
                  {plan.free_trial_days ? (
                    <div className="mb-3 inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] font-bold" data-testid={`plan-trial-${plan.id}`}>
                      <Check className="w-3.5 h-3.5" /> {plan.free_trial_days} giorni gratis · disdici quando vuoi
                    </div>
                  ) : null}

                  <ul className="space-y-0.5 flex-1">
                    {featureLabels(plan).map((f) => (
                      <FeatureRow key={f.key} included={!!f.on} label={f.label} />
                    ))}
                  </ul>

                  <PlanCTA plan={plan} isLogged={isLogged} discount={discount} cycle={cycle} />
                </div>
              );
            })}
          </div>
        )}

        {/* Free trial transparency disclosure (D.Lgs. 26/2023 Direttiva Omnibus) */}
        <div className="mt-10 text-center max-w-2xl mx-auto" data-testid="pricing-trial-disclosure">
          <p className="text-xs text-slate-500 leading-relaxed">
            ✓ <b>30 giorni di prova gratuita</b> su tutti i piani. Al termine della prova l&apos;abbonamento prosegue al prezzo del piano scelto.
            Puoi disdire in qualsiasi momento dall&apos;area personale, senza penali e senza vincoli.
          </p>
        </div>

        {/* Contact strip */}
        <div className="mt-12 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h4 className="text-lg font-black text-[#1E293B]">Hai più di un&apos;agenzia o esigenze specifiche?</h4>
            <p className="text-sm text-slate-500 mt-1">
              Studi multi-sede, network e società: scriviamoci, prepariamo una quotazione su misura.
            </p>
          </div>
          <a
            href="mailto:info@gostreet.it?subject=Richiesta%20preventivo%20GoStreet"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#2456C6] text-white font-bold text-sm hover:bg-[#1B47AC]"
            data-testid="pricing-contact-btn"
          >
            <Mail className="w-4 h-4" /> Contattaci
          </a>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-400 mt-10">
          Tutti i prezzi sono in Euro, IVA esclusa. Fatturazione a cura di TotalKlick srls.
        </p>
      </div>
    </div>
  );
}

// ---------- price sub-components ----------

function PriceBlockAnnual({ plan }) {
  const monthly = plan.price_annual_monthly_equiv || Math.round((plan.price_annual || 0) / 12);
  const discounted = plan.discount_pct_applied > 0;
  return (
    <div>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-black text-[#1E293B]" data-testid={`plan-price-${plan.id}`}>
          {formatEUR(monthly)}
        </span>
        <span className="text-sm text-slate-500">/ mese, fatturato annualmente</span>
      </div>
      {discounted && plan.price_annual_full && (
        <div className="mt-1 text-xs">
          <span className="text-slate-400 line-through mr-1">{formatEUR(Math.round(plan.price_annual_full / 12))}/mese</span>
          <span className="text-emerald-600 font-bold">
            Risparmi {formatEUR(plan.price_annual_full - plan.price_annual)} all&apos;anno
          </span>
        </div>
      )}
      {/* Setup fee */}
      {plan.setup_fee ? (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 text-slate-700" data-testid={`plan-setup-fee-${plan.id}`}>
          + {formatEUR(plan.setup_fee)} fee di ingresso (una tantum)
        </div>
      ) : null}
      {/* Additional branch (Enterprise) */}
      {plan.additional_branch_monthly ? (
        <div className="mt-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 text-[12px] text-slate-700" data-testid={`plan-branch-fee-${plan.id}`}>
          <b>+ {formatEUR(plan.additional_branch_monthly_equiv || plan.additional_branch_monthly)}/mese</b> per ogni filiale
          {plan.additional_branch_annual_full && plan.discount_pct_applied > 0 && (
            <span className="text-slate-400 line-through ml-1">
              ({formatEUR(Math.round(plan.additional_branch_annual_full / 12))}/mese)
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PriceBlockMonthly({ plan }) {
  return (
    <div>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-black text-[#1E293B]" data-testid={`plan-price-${plan.id}`}>
          {formatEUR(plan.price_monthly)}
        </span>
        <span className="text-sm text-slate-500">/ mese</span>
      </div>
      {/* Setup fee */}
      {plan.setup_fee ? (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 text-slate-700" data-testid={`plan-setup-fee-${plan.id}`}>
          + {formatEUR(plan.setup_fee)} attivazione (una tantum)
        </div>
      ) : null}
      {/* Additional branch (Enterprise) */}
      {plan.additional_branch_monthly ? (
        <div className="mt-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 text-[12px] text-slate-700" data-testid={`plan-branch-fee-${plan.id}`}>
          <b>+ {formatEUR(plan.additional_branch_monthly)}/mese</b> per ogni filiale
        </div>
      ) : null}
    </div>
  );
}

function PlanCTA({ plan, isLogged, discount, cycle }) {
  const [loading, setLoading] = useState(false);
  if (!isLogged) {
    return (
      <Link
        to="/register"
        data-testid={`plan-cta-${plan.id}`}
        className={`mt-6 block w-full text-center px-5 py-3 rounded-xl font-black text-sm transition ${
          plan.highlight
            ? "bg-[#F77F2A] text-white hover:bg-[#E26F1A]"
            : "bg-[#1E293B] text-white hover:bg-[#0F172A]"
        }`}
      >
        Registrati per vedere il prezzo
      </Link>
    );
  }
  const startCheckout = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.post(
        `${API}/billing/checkout-session`,
        {
          plan_id: plan.id,
          billing_cycle: cycle,
          num_branches: 0,
          origin_url: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Errore avvio checkout");
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      data-testid={`plan-cta-${plan.id}`}
      onClick={startCheckout}
      disabled={loading}
      className={`mt-6 block w-full text-center px-5 py-3 rounded-xl font-black text-sm transition disabled:opacity-60 disabled:cursor-not-allowed ${
        plan.highlight
          ? "bg-[#F77F2A] text-white hover:bg-[#E26F1A]"
          : "bg-[#1E293B] text-white hover:bg-[#0F172A]"
      }`}
    >
      {loading
        ? "Avvio checkout…"
        : cycle === "annual"
        ? (discount?.discount_pct > 0 ? `Attiva con sconto −${discount.discount_pct}%` : "Attiva piano annuale")
        : "Attiva piano mensile"}
    </button>
  );
}
